import { Flex } from 'reflexbox';
import styled from 'styled-components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import TreamsLogo from '../../../../../../../assets/logo.svg';
import BoundingBox from '../BoundingBox';
import Absolute from '../Absolute';

import { ICertificateProps } from './CertificateProps';

const ColorBackground = styled.div<{ color?: string; showOutlines: boolean }>`
	width: 100%;
	height: 100%;
	background-color: ${(props) => props.color ?? props.theme.colors.primary};
	padding: 16mm 16mm 0 16mm;
`;

const WhiteBackground = styled.div<{ showOutlines: boolean }>`
	width: 265mm;
	height: 178mm;
	background-color: ${(props) => props.theme.colors.white};
	border-radius: 3mm;
	position: relative;
`;

const Image = styled.img`
	max-width: 100%;
	max-height: 100%;
`;

function LogoBox({ showBounds, logo }: { showBounds: boolean; logo?: string }) {
	const { t } = useTranslation();

	return (
		<Absolute top={7} left={8} height={20} width={60}>
			<BoundingBox
				title={t('learning.certificates.outlineDesign.placeholders.logo')}
				showTitle={!logo}
				showBounds={showBounds}
			>
				{logo && (
					<Image src={logo} alt={t('learning.certificates.outlineDesign.placeholders.logo')} />
				)}
			</BoundingBox>
		</Absolute>
	);
}

const Title = styled.h1<{ color?: string }>`
	font-weight: 200;
	text-align: center;
	font-size: 6mm;
	color: ${(props) => props.color ?? props.theme.colors.primary};
`;

const CertificateTitle = styled.h1`
	font-size: 12mm;
	line-height: 15mm;
	text-align: center;
	-webkit-line-clamp: 2;
	line-clamp: 2;
	overflow: hidden;
	margin: 0;
	font-weight: bold;
`;

function CertificateTitleBox({ showBounds, title }: { showBounds: boolean; title?: string }) {
	const { t } = useTranslation();

	return (
		<Absolute top={30} left={20} right={20} height={30}>
			<BoundingBox
				title={t('learning.certificates.outlineDesign.placeholders.certificateTitle')}
				showTitle={!title}
				showBounds={showBounds}
			>
				<Flex flexDirection="column" justifyContent="center" alignItems="center" height="100%">
					<CertificateTitle>{title}</CertificateTitle>
				</Flex>
			</BoundingBox>
		</Absolute>
	);
}

const CertificateDescription = styled.p`
	display: block;
	font-size: 6mm;
	line-height: 8.5mm;
	text-overflow: ellipsis;
	-webkit-line-clamp: 4;
	line-clamp: 4;
	white-space: pre-wrap;
	overflow: hidden;
	color: ${(props) => props.theme.colors.slateBlue};
	text-align: center;
`;

function CertificateDescriptionBox({ showBounds, text }: { showBounds: boolean; text?: string }) {
	const { t } = useTranslation();

	return (
		<Absolute top={62} left={35} right={35} height={34}>
			<BoundingBox
				title={t('learning.certificates.outlineDesign.placeholders.certificateDescription')}
				showTitle={!text}
				showBounds={showBounds}
			>
				<Flex flexDirection="column" justifyContent="center" alignItems="center" height="100%">
					<CertificateDescription>{text}</CertificateDescription>
				</Flex>
			</BoundingBox>
		</Absolute>
	);
}

const PoweredByText = styled.span`
	color: ${(props) => props.theme.colors.white};
	opacity: 0.61;
	font-size: 6mm;
	margin-right: 4mm;
`;

const PoweredByLogo = styled.div`
	width: 35mm;
	height: 10mm;
`;

function PoweredByBox() {
	const { t } = useTranslation();

	return (
		<Absolute right={5} bottom={3} width={75} height={10}>
			<Flex justifyContent="flex-end" alignItems="center">
				<PoweredByText>{t('learning.certificates.outlineDesign.poweredBy')}</PoweredByText>
				<PoweredByLogo>
					<TreamsLogo />
				</PoweredByLogo>
			</Flex>
		</Absolute>
	);
}

function SignatureBox({ showBounds, signature }: { showBounds: boolean; signature?: string }) {
	const { t } = useTranslation();

	return (
		<Absolute bottom={20} left={20} width={60} height={20}>
			<BoundingBox
				title={t('learning.certificates.outlineDesign.placeholders.signature')}
				showTitle={!signature}
				showBounds={showBounds}
			>
				{signature && (
					<Image
						src={signature}
						alt={t('learning.certificates.outlineDesign.placeholders.signature')}
					/>
				)}
			</BoundingBox>
		</Absolute>
	);
}

const SignerText = styled.span`
	display: block;
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 5mm;
	line-height: 8mm;
`;

function SignerBox({
	showBounds,
	signerName,
	signerTitle,
}: {
	showBounds: boolean;
	signerName?: string;
	signerTitle?: string;
}) {
	const { t } = useTranslation();

	return (
		<Absolute bottom={4} left={20} width={125} height={16}>
			<BoundingBox
				title={t('learning.certificates.outlineDesign.placeholders.signer')}
				showTitle={!signerName && !signerTitle}
				showBounds={showBounds}
			>
				{signerName && <SignerText>{signerName}</SignerText>}
				{signerTitle && <SignerText>{signerTitle}</SignerText>}
			</BoundingBox>
		</Absolute>
	);
}

const AttendeeName = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 7mm;
	line-height: 10mm;
	text-align: center;
`;

function AttendeeNameBox({ showBounds, name }: { showBounds: boolean; name?: string }) {
	const { t } = useTranslation();

	return (
		<Absolute bottom={65} left={30} right={30} height={10}>
			<BoundingBox
				title={t('learning.certificates.outlineDesign.placeholders.attendee')}
				showTitle={!name}
				showBounds={showBounds}
			>
				<AttendeeName>{name}</AttendeeName>
			</BoundingBox>
		</Absolute>
	);
}

const HorizontalLine = styled.div`
	width: 100%;
	height: 0.5mm;
	background-color: ${(props) => props.theme.colors.slateBlue};
`;

const BottomText = styled.div`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 5mm;
	line-height: 8mm;
	text-align: center;
`;

function BottomTextBox({ showBounds, text }: { showBounds: boolean; text?: string }) {
	const { t } = useTranslation();

	return (
		<Absolute bottom={45} left={55} right={55} height={16}>
			<BoundingBox
				title={t('learning.certificates.outlineDesign.placeholders.bottomText')}
				showTitle={!text}
				showBounds={showBounds}
			>
				<BottomText>{text}</BottomText>
			</BoundingBox>
		</Absolute>
	);
}

export default function CertificateOutlineDesign({
	accentColor,
	logo,
	eventTitle,
	eventDescription,
	eventDate,
	attendeeName,
	signerName,
	signerTitle,
	signature,
	showBounds,
}: ICertificateProps & { showBounds: boolean }) {
	const { t } = useTranslation();

	return (
		<ColorBackground color={accentColor} showOutlines={showBounds}>
			<WhiteBackground showOutlines={showBounds}>
				<LogoBox showBounds={showBounds} logo={logo} />
				<Absolute top={15} left={50} right={50} height={5}>
					<Title color={accentColor}>{t('learning.certificates.outlineDesign.title')}</Title>
				</Absolute>
				<CertificateTitleBox showBounds={showBounds} title={eventTitle} />
				<CertificateDescriptionBox showBounds={showBounds} text={eventDescription} />
				<AttendeeNameBox showBounds={showBounds} name={attendeeName} />
				<Absolute bottom={63} left={75} right={75} height={1}>
					<HorizontalLine />
				</Absolute>
				<BottomTextBox
					showBounds={showBounds}
					text={t('learning.certificates.outlineDesign.bottomText', {
						date: eventDate ? moment(eventDate).format('LL') : '-',
						title: eventTitle ?? '-',
					})}
				/>
				<SignatureBox showBounds={showBounds} signature={signature} />
				<SignerBox showBounds={showBounds} signerName={signerName} signerTitle={signerTitle} />
			</WhiteBackground>
			<PoweredByBox />
		</ColorBackground>
	);
}
